import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';

import {
  fetchPayments,
  selectPayments,
  selectPaymentsLoading,
  fetchScheduleTypesFilters,
  selectScheduleTypesFilters,
  fetchXeroAccountsFilters,
  selectXeroAccountsFilters,
  fetchOneOffChargeReasonFilters,
  selectOneOffChargeReasonsFilters
} from 'ducks/payments';
import {exportCsvInBackground} from 'ducks/files';

import Page from 'components/Page';
import ListContent from 'components/ListContent';
import VisibleIf from 'components/VisibleIf';
import {Container} from 'containers/types';
import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';
import Loading from 'components/Loading';
import {withFilters, FILTERS, WithFiltersProps} from 'components/withFilters';
import {withSorting, SORTING, WithSortingProps} from 'components/withSorting';
import {
  withPagination,
  PAGINATION,
  WithPaginationProps
} from 'components/withPagination';

import {
  PaymentsListTable,
  SubNavigation,
  PaymentsListFilters
} from './components';
import {composeActions} from 'ducks/common';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import {reverse} from 'utils/urls';
import {BOOKING_DETAIL_URL} from 'config/urls';

import './styles.css';

export const initialFilters: {
  search: string;
  customer: string | null;
  booking: string | null;
  bookingStatuses: string[];
  xeroStatuses: string[];
  scheduleTypes: string[];
  xeroAccountsOptions: string[];
  createdBy: string | null;
  createdByTeam: number | null;
  paymentDateBefore: string;
  paymentDateAfter: string;
  invoiceDueDateBefore: string;
  invoiceDueDateAfter: string;
  invoiceStatuses: string[];
  createdAtBefore: string;
  createdAtAfter: string;
  oneOffChargeReasons: string[];
} = {
  search: '',
  customer: null,
  booking: null,
  bookingStatuses: [],
  xeroStatuses: [],
  scheduleTypes: [],
  xeroAccountsOptions: [],
  createdBy: null,
  createdByTeam: null,
  paymentDateBefore: '',
  paymentDateAfter: '',
  invoiceDueDateBefore: '',
  invoiceDueDateAfter: '',
  invoiceStatuses: [],
  createdAtBefore: '',
  createdAtAfter: '',
  oneOffChargeReasons: []
};

class PaymentsList extends React.Component<
  Container &
    LoginRequiredProps &
    WithPaginationProps &
    WithFiltersProps<typeof initialFilters> &
    WithSortingProps &
    ReturnType<typeof mapsStateToProps> &
    typeof mapDispatchToProps
> {
  componentDidMount() {
    const {match: {params: {locationRef}}} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_PAYMENTS_LIST,
        locationRef
      },
      () => {
        this.props.fetchPayments({locationRef});
        this.props.fetchScheduleTypesFilters({locationRef});
        this.props.fetchXeroAccountsFilters({locationRef});
        this.props.fetchOneOffChargeReasonFilters({locationRef});
      }
    );
  }

  onPageChange = ({offset, pageSize}) => {
    const {locationRef} = this.props.match.params;

    this.props.onPaginationChange({pageSize, offset}, [
      () => fetchPayments({locationRef})
    ]);
  };

  setFilter = (field, value) => {
    const {locationRef} = this.props.match.params;

    this.props.composeActions([
      () => this.props.setFilter(field, value),
      () => fetchPayments({locationRef})
    ]);
  };

  clearFilters = () => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    this.props.composeActions([
      () => this.props.clearFilters(),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchPayments({locationRef})
    ]);
  };

  onSort = fieldName => {
    const {locationRef} = this.props.match.params;
    const pagination = this.props.getPagination();

    this.props.onSort(fieldName, [
      () =>
        this.props.onPaginationChange({
          pageSize: pagination.pageSize,
          offset: 0
        }),
      () => fetchPayments({locationRef})
    ]);
  };

  redirectToBookingDetail = ({bookingId}) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        window.open(reverse(BOOKING_DETAIL_URL, {bookingId, locationRef}));
      }
    );
  };

  redirectToScheduleItem = ({link}) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS, locationRef},
      () => {
        window.open(link);
      }
    );
  };

  render() {
    const {
      match: {params: {locationRef}},
      paymentsLoading,
      payments,
      scheduleTypesFilters,
      xeroAccountsFilters,
      oneOffChargeReasonFilters
    } = this.props;

    return (
      <Page activeTab="payments" locationRef={locationRef}>
        <VisibleIf
          permission={PERMISSIONS.VIEW_PAYMENTS_LIST}
          locationRef={locationRef}>
          <ListContent
            className="PaymentsList"
            filters={this.props.getFilters()}
            setFilter={this.setFilter}
            initialFilters={initialFilters}
            navigationExtraElement={SubNavigation}>
            <ListContent.Content>
              <div className="mainContent">
                {paymentsLoading && <Loading />}
                {!paymentsLoading && (
                  <PaymentsListTable
                    payments={payments}
                    sort={this.props.getSort()}
                    onSort={this.onSort}
                    pagination={this.props.getPagination()}
                    onPageChange={this.onPageChange}
                    redirectToBookingDetail={this.redirectToBookingDetail}
                    redirectToScheduleItem={this.redirectToScheduleItem}
                    filters={this.props.getFilters()}
                    locationRef={locationRef}
                    exportCsvInBackground={this.props.exportCsvInBackground}
                  />
                )}
              </div>
            </ListContent.Content>
            <ListContent.Filters>
              <PaymentsListFilters
                locationRef={locationRef}
                setFilter={this.setFilter}
                clearFilters={this.clearFilters}
                getFilters={this.props.getFilters}
                scheduleTypesFilters={scheduleTypesFilters}
                xeroAccountsFilters={xeroAccountsFilters}
                oneOffChargeReasonFilters={oneOffChargeReasonFilters}
              />
            </ListContent.Filters>
          </ListContent>
        </VisibleIf>
      </Page>
    );
  }
}

const mapsStateToProps = state => {
  return {
    paymentsLoading: selectPaymentsLoading(state),
    payments: selectPayments(state),
    scheduleTypesFilters: selectScheduleTypesFilters(state),
    xeroAccountsFilters: selectXeroAccountsFilters(state),
    oneOffChargeReasonFilters: selectOneOffChargeReasonsFilters(state)
  };
};

const mapDispatchToProps = {
  fetchPayments,
  fetchScheduleTypesFilters,
  fetchXeroAccountsFilters,
  fetchOneOffChargeReasonFilters,
  composeActions,
  requiresPermission,
  exportCsvInBackground
};

const authenticatedComponent = LoginRequired(PaymentsList);

const componentWithPagination = withPagination(
  authenticatedComponent,
  PAGINATION.PAYMENTS_LIST,
  {
    saveToUrl: true,
    pageSizes: [20, 50, 100],
    pageSize: 20
  }
);

const componentWithPaginationAndFilters = withFilters(
  componentWithPagination,
  FILTERS.PAYMENTS_LIST,
  {initialFilters, saveToUrl: true}
);

const componentWithPaginationAndFiltersAndSorting = withSorting(
  componentWithPaginationAndFilters,
  SORTING.PAYMENTS_LIST,
  {saveToUrl: true}
);

export default connect(mapsStateToProps, mapDispatchToProps)(
  componentWithPaginationAndFiltersAndSorting
);
